import React, { useMemo, useState } from 'react';
import { Theme } from '../../Enums/Theme.enum';
import useNotification from '../../Hooks/useNotification.hook';
import {
	acceptCase,
	connectWithPatient,
} from '../../Modules/Case/Services/case.service';
import { useAppContext } from '../../Services/AppContext';
import LinkButton from '../Button/LinkButton';
import ContextMenu from '../ContextMenu/ContextMenu';
import TreatmentModal from '../Treatment/TreatmentModal';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';

// @ts-ignore
import styles from './CalendarContextMenu.module.scss';
import RemoveConfirmationModal from '../RemoveConfirmation/RemoveConfirmationModal';
import useTranslation from '../../Hooks/useTranslation';
import { Moment } from 'moment';
import { Meta, useDoneEventMutation } from '../../graphql/graphql';

interface CalendarContextMenuProps {
	contextParams: object;
	event: any;
	onEdit: () => void;
	onRemove: (event: object) => void;
	onContextClose: () => void;
	onRemoveCancel: () => void;
	onCreateNewEvent: (date: Moment) => void;
	allowConnectWithPatient?: boolean;
	allowShowPatientProfile?: boolean;
	removeConfirmationText: string;
	removeConfirmationTitle: string;
	onMarkEventDone: () => void;
}

const CalendarContextMenu: React.FC<CalendarContextMenuProps> = ({
	contextParams,
	event,
	onEdit,
	onRemove,
	onContextClose,
	onRemoveCancel,
	onCreateNewEvent,
	allowConnectWithPatient = true,
	allowShowPatientProfile = true,
	removeConfirmationText,
	removeConfirmationTitle,
	onMarkEventDone,
}) => {
	const [treatmentOpen, setTreatmentOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [removeConfirmation, setRemoveConfirmation] = useState(false);
	const patientId = event?.event?.extendedProps.patient.id;
	const loggedUserId = useAppContext().userData?.id;
	const workplaceId = useAppContext().userData?.workplaceId;
	const caseId = event?.event?.extendedProps.caseId;
	const meta = event?.event?.extendedProps.meta;
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const [markEventDone] = useDoneEventMutation();

	const handleConnectWithPatient = async () => {
		try {
			setLoading(true);
			if (caseId) {
				await acceptCase(caseId, loggedUserId);
			} else {
				await connectWithPatient(loggedUserId, patientId, workplaceId);
			}
		} catch (e: any) {
			if (e.response.data.displayableError) {
				setLoading(false);
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
	};

	const handleOpenPatientProfile = () => {
		setTreatmentOpen(true);
		onContextClose && onContextClose();
	};

	const handleEditEvent = () => {
		onEdit && onEdit();
		onContextClose && onContextClose();
	};

	const handleOpenRemoveConfirmation = () => {
		setRemoveConfirmation(true);
		onContextClose && onContextClose();
	};

	const handleRemoveEvent = () => {
		setRemoveConfirmation(false);
		onRemove && onRemove(event);
		showNotificationSuccess({
			content: t('base.deleted'),
		});
	};

	if (!event) {
		return null;
	}

	const handleCancelRemove = () => {
		setRemoveConfirmation(false);
		onRemoveCancel();
	};

	const handleCreateNewEvent = (date: Moment) => {
		onCreateNewEvent(date);
		onContextClose && onContextClose();
	};

	const handleMarkAsDone = (eventId: string) => {
		markEventDone({
			input: {
				eventId: eventId,
			},
		}).unwrap();
		onMarkEventDone();
	};

	const flagDone = useMemo(() => {
		return meta?.some((item: Meta) => item?.key === 'flag_done');
	}, [meta]);

	return (
		<>
			{loading && <FullPageLoading />}
			<ContextMenu
				params={contextParams}
				onContextClose={onContextClose}
				openInModalRoot={true}
				customRootName='context-menu-root'>
				{allowConnectWithPatient ? (
					<>
						<LinkButton
							onClick={() => {
								handleConnectWithPatient();
							}}
							title={'base.shared.contactAPatient'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '10px' }} />
					</>
				) : (
					<></>
				)}
				{allowShowPatientProfile ? (
					<>
						<LinkButton
							onClick={() => {
								handleOpenPatientProfile();
							}}
							title={'base.showPatientsCard'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '10px' }} />
					</>
				) : (
					<></>
				)}
				<LinkButton
					onClick={() => {
						handleEditEvent();
					}}
					title={'base.shared.edit'}
					theme={Theme.BLUE}
					firstUpper
				/>
				<LinkButton
					onClick={() => handleCreateNewEvent(event?.event?.start)}
					title={'component.calendarContextMenu.createNewAppointment'}
					theme={Theme.BLUE}
					firstUpper
				/>
				{!flagDone ? (
					<LinkButton
						onClick={() => handleMarkAsDone(event?.event?.id)}
						title={'component.calendarContextMenu.markAsDone'}
						theme={Theme.BLUE}
						firstUpper
					/>
				) : (
					<></>
				)}
				<div style={{ marginTop: '20px' }} />
				<LinkButton
					title='base.shared.delete'
					theme={Theme.RED}
					onClick={handleOpenRemoveConfirmation}
					firstUpper
				/>
			</ContextMenu>
			<RemoveConfirmationModal
				opened={removeConfirmation}
				onClose={handleCancelRemove}
				onConfirm={handleRemoveEvent}
				confirmationText={removeConfirmationText}
				confirmationTitle={removeConfirmationTitle}
			/>
			<TreatmentModal
				opened={treatmentOpen}
				onClose={() => setTreatmentOpen(false)}
				patientId={patientId}
				workplaceId={workplaceId}
				loggedUserId={loggedUserId}
				caseId={caseId}
			/>
		</>
	);
};

export default CalendarContextMenu;
